import {
  createModuleInstance,
  effectiveRarity,
  type ModuleInstance,
} from "../models/ModuleInstance.js";
import { RARITY_ORDER, type ModuleDefinition, type ModuleRarity } from "../modules/ProjectileModule.js";
import type { ModuleInventory } from "./ModuleInventory.js";
import type { ModuleGridController } from "./ModuleGridController.js";

/**
 * 모듈 합성(머지) 순수 로직 — Phaser 비의존(단위 테스트 대상).
 * "같은 이름(definitionId) + 같은 유효등급(effectiveRarity) 3개 → 같은 이름·다음 등급 1개".
 * 등급은 ModuleInstance.rarity 오버라이드로 표현되며, 전투 효과는 DefinitionModule이 등급별 effect로 적용한다.
 * 재료는 전투 그리드에 배치되지 않은 인스턴스만(unplacedMergeCandidates) — 배치 중인 모듈은 보호한다.
 */

/** 등급 진행 순서 (낮음→높음). 합성은 한 단계씩 상승. legendary=최고(합성 결과 없음). RARITY_ORDER가 SSoT. */
export const RARITY_PROGRESSION = RARITY_ORDER;

/** 합성에 필요한 재료 개수 (같은 이름·같은 등급 N개 → 다음 등급 1개). */
export const MERGE_MATERIAL_COUNT = 3;

/** definitionId → 정의 조회 콜백 (ModuleRegistry.getDefinition 위임). effective rarity 산출용. */
export type DefinitionLookup = (definitionId: string) => ModuleDefinition | undefined;

/** 다음 등급 (legendary 등 최고면 null = 더 합성 불가). */
export function nextRarity(rarity: ModuleRarity): ModuleRarity | null {
  const i = RARITY_PROGRESSION.indexOf(rarity);
  if (i < 0 || i >= RARITY_PROGRESSION.length - 1) return null;
  return RARITY_PROGRESSION[i + 1]!;
}

/**
 * 합성 가능 여부 — 재료가 정확히 MERGE_MATERIAL_COUNT개 + 모두 같은 definitionId + 모두 같은 effective rarity
 * + 그 등급이 최고(legendary)가 아님. 하나라도 어긋나면 false.
 */
export function canMerge(materials: readonly ModuleInstance[], defOf: DefinitionLookup): boolean {
  if (materials.length !== MERGE_MATERIAL_COUNT) return false;
  if (materials.some((m) => m.fused)) return false; // 융합 산출물은 합성 재료 제외(다음부터 합성·융합 제외)
  const first = materials[0]!;
  const baseRarity = effectiveRarity(first, defOf(first.definitionId));
  if (nextRarity(baseRarity) === null) return false; // 최고 등급은 합성 결과 없음
  return materials.every(
    (m) =>
      m.definitionId === first.definitionId &&
      effectiveRarity(m, defOf(m.definitionId)) === baseRarity,
  );
}

/** 정보 팝업 등록 버튼이 참조하는 모듈 행의 최소 식별 필드 (ModuleMergeRow가 구조적 호환). */
export interface MergeSelectableRow {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly rarity: ModuleRarity;
}

/** 등록 버튼 동작 — 미등록이면 register, 이미 재료면 unregister(해제). */
export type MaterialButtonAction = "register" | "unregister";

/** 등록 버튼 상태 — 표시할 동작(action)과 클릭 가능 여부(enabled). resolveMaterialButtonState 반환값. */
export interface MaterialButtonState {
  readonly action: MaterialButtonAction;
  readonly enabled: boolean;
}

/**
 * 정보 팝업 등록 버튼 상태 — 합성 탭 셀의 정보 팝업 하단 버튼이 "등록/해제/불가"를 결정한다 (순수).
 * - 이미 선택된 재료 → unregister (해제 가능, 슬롯 만석이어도 자기 자신은 뺄 수 있음)
 * - 미선택: 슬롯 만석이거나 기준(첫 선택)과 definitionId·rarity가 다르면 register-disabled, 그 외 register-enabled
 * - rows에 없는 targetId(이미 소비/이동)는 register-disabled로 방어.
 */
export function resolveMaterialButtonState(
  selectedIds: readonly string[],
  rows: readonly MergeSelectableRow[],
  targetId: string,
  materialCount: number = MERGE_MATERIAL_COUNT,
): MaterialButtonState {
  const target = rows.find((r) => r.instanceId === targetId);
  if (!target) return { action: "register", enabled: false };
  if (selectedIds.includes(targetId)) return { action: "unregister", enabled: true };
  if (selectedIds.length >= materialCount) return { action: "register", enabled: false };
  const baseId = selectedIds[0];
  const base = baseId ? rows.find((r) => r.instanceId === baseId) : undefined;
  if (base && (target.definitionId !== base.definitionId || target.rarity !== base.rarity)) {
    return { action: "register", enabled: false };
  }
  return { action: "register", enabled: true };
}

export interface MergeResult {
  readonly merged: ModuleInstance;
  readonly consumedIds: readonly string[];
}

/**
 * 재료를 합성해 같은 definitionId·다음 등급 인스턴스 1개를 만든다 (순수).
 * footprintId = 재료[0](결정론·UX 혼란 최소). ⚠ 레벨 보존 로직 없음 — 레벨은 moduleId별 플레이어 자산
 * (PlayerModuleLevelController)이라 같은 definitionId 산출물이 자동으로 같은 player level을 공유한다.
 * 호출 전 canMerge로 가드 — 최고 등급이면 throw(논리 오류 방지).
 */
export function mergeModules(
  materials: readonly ModuleInstance[],
  defOf: DefinitionLookup,
  nextInstanceId: string,
): MergeResult {
  const first = materials[0]!;
  const baseRarity = effectiveRarity(first, defOf(first.definitionId));
  const next = nextRarity(baseRarity);
  if (next === null) {
    throw new Error(`[ModuleMerge] cannot merge max-rarity module: ${first.definitionId}`);
  }
  const merged = createModuleInstance({
    definitionId: first.definitionId,
    instanceId: nextInstanceId,
    rarity: next,
    footprintId: first.footprintId,
  });
  return { merged, consumedIds: materials.map((m) => m.instanceId) };
}

/**
 * 합성 재료 후보 — 전투 그리드에 배치되지 않은 보유 인스턴스만 (배치 중인 모듈은 보호).
 * 합성 탭 하단 그리드 표시 + 합성 직전 재검증의 SSoT.
 */
export function unplacedMergeCandidates(
  inventory: ModuleInventory,
  grid: ModuleGridController,
): ModuleInstance[] {
  const placed = new Set(grid.placedInstanceIds());
  // 융합 산출물(i.fused)은 합성 재료에서도 제외 — "다음부터 합성·융합에서 제외"(사용자 규칙).
  return inventory.allInstances().filter((i) => !placed.has(i.instanceId) && !i.fused);
}
